import { useMutation, useQueryClient } from '@tanstack/react-query';

import type {
  PatchAdminV1StoresStoreIdProductsIdBody,
  PostAdminV1StoresStoreIdProductsBody,
  PutAdminV1StoresStoreIdProductsBulkPublishBody,
} from '@nucleus-commerce/api-client/admin';

import { collectionKeys } from '@/features/collections';
import { productKeys } from '@/features/products';

import { storeCatalogApi } from '../api/store-catalog-api';

import { storeCatalogKeys } from './store-catalog-keys';

/**
 * A publication changed: lists and memberships of the store refetch; the master product's
 * `publishedAt` may have moved too, and so may the store's collections (rules match published
 * products, removal drops manual memberships).
 */
function refreshStore(queryClient: ReturnType<typeof useQueryClient>, storeId: string) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: storeCatalogKeys.lists(storeId) }),
    queryClient.invalidateQueries({ queryKey: productKeys.lists() }),
    queryClient.invalidateQueries({ queryKey: collectionKeys.all(storeId) }),
  ]);
}

/** 409s (non-active master, handle or product taken) are rendered where the action sits. */
export function useAddStoreProduct(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PostAdminV1StoresStoreIdProductsBody) =>
      storeCatalogApi.create(storeId, body),
    meta: { successMessage: 'storeCatalog:toasts.added', silentError: true },
    onSuccess: (row) => {
      queryClient.setQueryData(storeCatalogKeys.detail(storeId, row.id), row);
      return refreshStore(queryClient, storeId);
    },
  });
}

/**
 * The same add, for a modal that adds one product to several stores: the caller says which store
 * per call, renders each store's failure and sums up the successes in one toast.
 */
export function useAddProductToStore() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      storeId,
      body,
    }: {
      storeId: string;
      body: PostAdminV1StoresStoreIdProductsBody;
    }) => storeCatalogApi.create(storeId, body),
    meta: { silentError: true },
    onSuccess: (row, { storeId }) => {
      queryClient.setQueryData(storeCatalogKeys.detail(storeId, row.id), row);
      return refreshStore(queryClient, storeId);
    },
  });
}

export function useUpdateStoreProduct(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PatchAdminV1StoresStoreIdProductsIdBody }) =>
      storeCatalogApi.update(storeId, id, body),
    meta: { successMessage: 'storeCatalog:toasts.updated', silentError: true },
    onSuccess: (row) => {
      queryClient.setQueryData(storeCatalogKeys.detail(storeId, row.id), row);
      return refreshStore(queryClient, storeId);
    },
  });
}

export function useRemoveStoreProduct(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => storeCatalogApi.remove(storeId, id),
    // The confirm dialog renders the failure next to its button.
    meta: { successMessage: 'storeCatalog:toasts.removed', silentError: true },
    onSuccess: (_void, id) => {
      queryClient.removeQueries({ queryKey: storeCatalogKeys.detail(storeId, id) });
      return refreshStore(queryClient, storeId);
    },
  });
}

/** The result lists skipped products with a reason and callers render failures themselves, so no toast here. */
export function useBulkPublish(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PutAdminV1StoresStoreIdProductsBulkPublishBody) =>
      storeCatalogApi.bulkPublish(storeId, body),
    meta: { silentError: true },
    onSuccess: () =>
      Promise.all([
        refreshStore(queryClient, storeId),
        queryClient.invalidateQueries({ queryKey: storeCatalogKeys.details(storeId) }),
      ]),
  });
}
